import React, { ReactElement, PropsWithChildren, useState } from 'react';
import { useRouter } from 'next/router';
import { Icon } from '@pb/shared/ui/atoms/icon';
import {
    TStep,
    TStepBasicInfo,
    TStepperAccordionProperties,
} from './stepper-accordion.types';
import {
    StyledAccordion,
    StyledAccordionHeading,
    StyledAccordionHeadingContainer,
    StyledActionNeededIcon,
    StyledActionNeededStepName,
    StyledAllStepsContainer,
    StyledCurrentStepIcon,
    StyledCurrentStepName,
    StyledHeadingStepName,
    StyledHeadingStepNumber,
    StyledIconConnector,
    StyledInvisibleIconConnector,
    StyledStepDetailsContainer,
    StyledStepIconsContainer,
    StyledFutureStepName,
    StyledBasicInfoStepNumber,
    StyledFutureStepNumber,
    StyledSuccessIcon,
    StyledSuccessStepName,
} from './stepper-accordion.styled';

export function StepperAccordion({
    steps,
}: PropsWithChildren<TStepperAccordionProperties>): ReactElement {
    const router = useRouter();

    const [accordionIcon, setAccordionIcon] = useState<'arrowUp' | 'arrowDown'>(
        'arrowDown'
    );

    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [showAllSteps, setShowAllSteps] = useState<boolean>(false);

    function handleAccordionHeadingClick() {
        isExpanded ? collapseStepperAccordion() : expandStepperAccordion();
        setIsExpanded(!isExpanded);
    }

    function collapseStepperAccordion() {
        setAccordionIcon('arrowDown');
        setShowAllSteps(false);
    }

    function expandStepperAccordion() {
        setAccordionIcon('arrowUp');
        setShowAllSteps(true);
    }

    function renderCorrectStylingForIcon(step: TStep, stepNumber: number) {
        if (step.position === 'Previous' && step.isComplete) {
            return (
                <StyledSuccessIcon>
                    <Icon type="tick" color="white" />
                </StyledSuccessIcon>
            );
        }
        if (step.position === 'Previous' && !step.isComplete) {
            return <StyledActionNeededIcon>!</StyledActionNeededIcon>;
        }
        if (step.position === 'Current') {
            return <StyledCurrentStepIcon>{stepNumber}</StyledCurrentStepIcon>;
        }
        return <StyledFutureStepNumber>{stepNumber}</StyledFutureStepNumber>;
    }

    function renderCorrectStylingForStepName(step: TStep) {
        if (step.position === 'Previous' && step.isComplete) {
            return <StyledSuccessStepName>{step.name}</StyledSuccessStepName>;
        }
        if (step.position === 'Previous' && !step.isComplete) {
            return (
                <StyledActionNeededStepName>
                    {step.name}
                </StyledActionNeededStepName>
            );
        }
        if (step.position === 'Current') {
            return <StyledCurrentStepName>{step.name}</StyledCurrentStepName>;
        }
        return <StyledFutureStepName>{step.name}</StyledFutureStepName>;
    }

    function renderAllSteps() {
        return (
            <>
                {steps.map((step, index) => {
                    const stepNumber = index + 1;
                    return (
                        <StyledAllStepsContainer
                            key={`${stepNumber}-${step.name}`}
                        >
                            <StyledStepIconsContainer>
                                {stepNumber === 1 && (
                                    <StyledInvisibleIconConnector />
                                )}
                                {stepNumber !== 1 && <StyledIconConnector />}
                                {renderCorrectStylingForIcon(step, stepNumber)}
                                {stepNumber !== steps.length && (
                                    <StyledIconConnector />
                                )}
                                {stepNumber === steps.length && (
                                    <StyledInvisibleIconConnector />
                                )}
                            </StyledStepIconsContainer>
                            <StyledStepDetailsContainer
                                onClick={() => {
                                    if (step.position === 'Previous') {
                                        router.push(step.asPath);
                                    }
                                }}
                                position={step.position}
                            >
                                <StyledBasicInfoStepNumber
                                    marginBottom="none"
                                    marginTop="none"
                                >
                                    Step {stepNumber}
                                </StyledBasicInfoStepNumber>
                                {renderCorrectStylingForStepName(step)}
                            </StyledStepDetailsContainer>
                        </StyledAllStepsContainer>
                    );
                })}
            </>
        );
    }

    function getCurrentStep(): TStepBasicInfo {
        const currentStep: TStepBasicInfo = {
            stepName: '',
            stepNumber: 0,
        };

        steps.forEach((step, index) => {
            if (step.position === 'Current') {
                currentStep.stepName = step.name;
                currentStep.stepNumber = index + 1;
            }
        });

        return currentStep;
    }

    return (
        <StyledAccordion>
            <StyledAccordionHeadingContainer
                onClick={handleAccordionHeadingClick}
            >
                <StyledAccordionHeading>
                    <StyledHeadingStepNumber
                        marginBottom="none"
                        marginTop="none"
                    >
                        Step {getCurrentStep().stepNumber} of {steps.length}
                    </StyledHeadingStepNumber>
                    <StyledHeadingStepName size="xsmall">
                        {getCurrentStep().stepName}
                    </StyledHeadingStepName>
                </StyledAccordionHeading>
                <Icon type={accordionIcon} />
            </StyledAccordionHeadingContainer>
            {showAllSteps && renderAllSteps()}
        </StyledAccordion>
    );
}
